/**
 * Shared localforage (IndexedDB-backed) storage loader.
 *
 * Extracted from `redux/createStore.ts`'s `loadLocalForage`/`getLocalForage` so the TanStack
 * Query user-state persister (`query/user-state/userStatePersister.ts`) reads/writes the exact
 * same underlying storage backend Redux's `redux-persist` already uses, instead of duplicating
 * this logic. redux/createStore.ts is intentionally left untouched during TEC-404 (Redux stays
 * live until every call site is migrated in later work) and still carries its own
 * near-identical copy — once Redux is fully removed, that copy should be deleted in favor of
 * this one.
 *
 * This has real, non-obvious interop handling — get it wrong and every persistence op silently
 * no-ops:
 * - a module-level singleton promise/instance, so `localforage` (a real IndexedDB open + driver
 *   negotiation) is only ever loaded once per page.
 * - SSR no-ops via a no-op storage object when `typeof window === 'undefined'` — there is no
 *   IndexedDB on the server, and importing `localforage` there would throw.
 * - a CJS/UMD fallback for when Vite dev serves the raw UMD build and the ESM import has no
 *   `default` export, in which case localforage lands on `window.localforage` instead.
 *
 * Lives under `utils/persistence/` rather than `utils/storage/` — this monorepo's root
 * `.gitignore` has a bare `storage` entry (intended for `scripts/duunitori_crawler/storage`,
 * the crawler's local data directory) that, having no leading slash, matches a directory named
 * `storage` at any depth — including `web-app/utils/storage/`. Naming it `persistence` avoids
 * silently gitignoring legitimate source files.
 */

export interface LocalForageStorage {
  getItem: (key: string) => Promise<unknown>;
  setItem: (key: string, value: unknown) => Promise<unknown>;
  removeItem: (key: string) => Promise<unknown>;
  clear: () => Promise<unknown>;
}

// Shared state for the localforage instance (module-level to persist across calls).
let localForagePromise: Promise<unknown> | null = null;
let localForageInstance: LocalForageStorage | null = null;

/** Lazy loader for localforage — dynamically imports only on the client side. */
export const loadLocalForage = async (): Promise<LocalForageStorage | null> => {
  if (typeof window === 'undefined') {
    return null; // Server-side: no localforage.
  }

  if (localForageInstance) {
    return localForageInstance; // Already loaded.
  }

  if (!localForagePromise) {
    // Start loading localforage.
    localForagePromise = import('localforage')
      .then((m) => {
        // CJS/UMD interop: in some bundling modes (e.g. Vite dev serving the raw UMD file) the
        // ESM namespace has no `default` and localforage lands on `window.localforage`. Without
        // this fallback every persistence op silently no-ops.
        const instance =
          (m as { default?: unknown }).default ??
          (typeof window !== 'undefined' ? (window as { localforage?: unknown }).localforage : null);
        if (!instance) {
          console.error(
            '[get-localforage-instance] localforage loaded without a usable export — persistence disabled'
          );
          return null;
        }
        localForageInstance = instance as LocalForageStorage;
        return localForageInstance;
      })
      .catch((err) => {
        console.error('[get-localforage-instance] Failed to load localforage:', err);
        localForagePromise = null; // Reset on error so we can retry.
        return null;
      });
  }

  return localForagePromise as Promise<LocalForageStorage | null>;
};

// Pre-load localforage on the client side as soon as this module loads.
if (typeof window !== 'undefined') {
  loadLocalForage().catch(() => {
    // Silently fail - will use no-op storage.
  });
}

/**
 * Returns a storage-like adapter that works with both `redux-persist`'s `Storage` interface and
 * the TanStack Query persister's `getItem`/`setItem`/`removeItem` shape. Methods handle the case
 * where localforage isn't loaded yet by awaiting `loadLocalForage()`.
 */
export const getLocalForageStorage = (): LocalForageStorage => {
  if (typeof window === 'undefined') {
    // Server-side: return a no-op storage object.
    return {
      getItem: () => Promise.resolve(null),
      setItem: () => Promise.resolve(null),
      removeItem: () => Promise.resolve(null),
      clear: () => Promise.resolve(null),
    };
  }

  // Client-side: return a storage adapter that uses the loaded instance, waiting for it to load
  // first if necessary.
  return {
    getItem: (key: string) => {
      if (localForageInstance) {
        return localForageInstance.getItem(key);
      }
      return loadLocalForage().then((instance) => (instance ? instance.getItem(key) : Promise.resolve(null)));
    },
    setItem: (key: string, value: unknown) => {
      if (localForageInstance) {
        return localForageInstance.setItem(key, value);
      }
      return loadLocalForage().then((instance) =>
        instance ? instance.setItem(key, value) : Promise.resolve(null)
      );
    },
    removeItem: (key: string) => {
      if (localForageInstance) {
        return localForageInstance.removeItem(key);
      }
      return loadLocalForage().then((instance) => (instance ? instance.removeItem(key) : Promise.resolve(null)));
    },
    clear: () => {
      if (localForageInstance) {
        return localForageInstance.clear();
      }
      return loadLocalForage().then((instance) => (instance ? instance.clear() : Promise.resolve(null)));
    },
  };
};

/** Resets the module-level localforage singleton (for tests only). */
export function resetLocalForageInstanceForTests(): void {
  localForageInstance = null;
  localForagePromise = null;
}
